import React, { useState, useEffect, useRef } from "react";
import { X, Plus, Trash2, ChevronDown, ChevronUp, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import SelectMenu from "./crm/SelectMenu";
function RaiseInvoiceForm({ isOpen, onClose, onSubmit, cases, clients, isSchedule, currentSeries = "1", isEditMode = false, editData = null }) {
  const navigate = useNavigate();
  const [resourceType, setResourceType] = useState("none");
  const [formData, setFormData] = useState({
    resourceName: "",
    selectedCase: null,
    contactId: "",
    contactName: "",
    currency: "INR",
    dueDate: "",
    particulars: [{ id: 1, description: "", amount: "" }],
    selectedExpenses: [],
    totalAmount: 0,
    invoiceNumber: "",
  });
  const [caseExpenses, setCaseExpenses] = useState([]);
  const [showResourceParticulars, setShowResourceParticulars] = useState(true);
  const [showCaseDropdown, setShowCaseDropdown] = useState(false);
  const [caseSearchTerm, setCaseSearchTerm] = useState("");
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const caseDropdownRef = useRef(null);
  const contactDropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen && !isEditMode) {
      // Generate invoice number using currentSeries
      const year = new Date().getFullYear();
      const month = new Date().getMonth() + 1;
      const invoiceNo = `${year}-${month}/${currentSeries}`;
      console.log("=== Invoice Number Generation ===");
      console.log("Current Series:", currentSeries);
      console.log("Generated Invoice Number:", invoiceNo);
      setFormData((prev) => ({ ...prev, invoiceNumber: invoiceNo }));
    }
  }, [isOpen, currentSeries, isEditMode]);

  // Pre-fill form data when editing
  useEffect(() => {
    if (isOpen && isEditMode && editData) {
      console.log("=== Edit Mode: Pre-filling form ===", editData);

      // Determine resource type
      const type = editData.resourceType || "none";
      setResourceType(type);

      // Find the selected case if it's a case invoice
      let selectedCase = null;
      if (type === "case" && editData.selectedCase) {
        selectedCase = cases.find(c => c.id === editData.selectedCase.id) || editData.selectedCase;

        // Load case expenses if available
        const expenses = selectedCase.expenses || [];
        setCaseExpenses(expenses);
      }

      // Pre-fill form data
      setFormData({
        resourceName: editData.resourceName || "",
        selectedCase: selectedCase,
        contactId: editData.contactId || "",
        contactName: editData.contactName || "",
        currency: editData.currency || "INR",
        dueDate: editData.dueDate || "",
        particulars: editData.particulars && editData.particulars.length > 0
          ? editData.particulars.map((p, idx) => ({ ...p, id: p.id || idx + 1 }))
          : [{ id: 1, description: "", amount: "" }],
        selectedExpenses: editData.selectedExpenses || [],
        totalAmount: editData.totalAmount || 0,
        invoiceNumber: editData.invoiceNumber || "",
      });
    }
  }, [isOpen, isEditMode, editData, cases]);

  useEffect(() => {
    calculateTotal();
  }, [formData.particulars, formData.selectedExpenses]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (caseDropdownRef.current && !caseDropdownRef.current.contains(event.target)) {
        setShowCaseDropdown(false);
      }
      if (contactDropdownRef.current && !contactDropdownRef.current.contains(event.target)) {
        setShowContactDropdown(false);
      }
    };

    if (showCaseDropdown || showContactDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCaseDropdown, showContactDropdown]);

  const calculateTotal = () => {
    const particularsTotal = formData.particulars.reduce(
      (sum, item) => sum + (parseFloat(item.amount) || 0),
      0
    );
    const expensesTotal = formData.selectedExpenses.reduce(
      (sum, expenseId) => {
        const expense = caseExpenses.find((e) => e.id === expenseId);
        return sum + (expense ? parseFloat(expense.amount) || 0 : 0);
      },
      0
    );
    const total = particularsTotal + expensesTotal;
    setFormData((prev) => ({ ...prev, totalAmount: total }));
  };

  const handleResourceTypeChange = (type) => {
    setResourceType(type);
    if (type === "none") {
      setFormData((prev) => ({
        ...prev,
        selectedCase: null,
        resourceName: "",
        selectedExpenses: [],
      }));
      setCaseExpenses([]);
    } else {
      setFormData((prev) => ({
        ...prev,
        resourceName: "",
        particulars: [],
      }));
    }
  };

  const handleCaseSelect = (caseItem) => {
    setFormData((prev) => ({
      ...prev,
      selectedCase: caseItem,
      resourceName: `${caseItem.petitionerName} VS ${caseItem.respondentName}`,
      selectedExpenses: [],
    }));

    // Load case expenses
    const expenses = caseItem.expenses || [];
    setCaseExpenses(expenses);
    setShowCaseDropdown(false);
    setCaseSearchTerm("");
  };

  const getFilteredCases = () => {
    if (!caseSearchTerm) return cases;

    const searchLower = caseSearchTerm.toLowerCase();
    return cases.filter((caseItem) => {
      const caseText = `${caseItem.caseNo} ${caseItem.petitionerName} ${caseItem.respondentName}`.toLowerCase();
      return caseText.includes(searchLower);
    });
  };

  const handleContactSelect = (contactId) => {
    const selectedClient = clients.find((c) => c.id === contactId);
    if (selectedClient) {
      const fullName = selectedClient.salutation
        ? `${selectedClient.salutation} ${selectedClient.name}`
        : selectedClient.name;
      setFormData((prev) => ({
        ...prev,
        contactId: contactId,
        contactName: fullName,
      }));
    }
  };

  const handleExpenseToggle = (expenseId) => {
    setFormData((prev) => {
      const isSelected = prev.selectedExpenses.includes(expenseId);
      const newSelectedExpenses = isSelected
        ? prev.selectedExpenses.filter((id) => id !== expenseId)
        : [...prev.selectedExpenses, expenseId];
      return {
        ...prev,
        selectedExpenses: newSelectedExpenses,
      };
    });
  };

  const addParticular = () => {
    setFormData((prev) => ({
      ...prev,
      particulars: [
        ...prev.particulars,
        { id: Date.now(), description: "", amount: "" },
      ],
    }));
  };

  const removeParticular = (id) => {
    if (formData.particulars.length === 1) {
      toast.error("At least one particular is required");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      particulars: prev.particulars.filter((p) => p.id !== id),
    }));
  };

  const updateParticular = (id, field, value) => {
    if (field === "amount") {
      // Allow only valid decimal number input (digits and single dot)
      if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    }
    setFormData((prev) => ({
      ...prev,
      particulars: prev.particulars.map((p) =>
        p.id === id ? { ...p, [field]: value } : p
      ),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.contactId) {
      toast.error("Please select a contact");
      return;
    }

    if (!formData.dueDate) {
      toast.error("Please select a due date");
      return;
    }

    if (resourceType === "case" && !formData.selectedCase) {
      toast.error("Please select a case");
      return;
    }

    if (resourceType === "none" && formData.particulars.some((p) => !p.description || !p.amount)) {
      toast.error("Please fill all particulars");
      return;
    }

    if (resourceType === "case" && formData.selectedExpenses.length === 0 && formData.particulars.length === 0) {
      toast.error("Please select expenses or add particulars");
      return;
    }

    const invoiceData = {
      ...formData,
      resourceType,
      dueAmount: formData.totalAmount,
    };

    onSubmit(invoiceData);

    // Don't reset form in edit mode - parent component handles cleanup
    if (!isEditMode) {
      resetForm();
    }
  };

  const resetForm = () => {
    setResourceType("none");
    setFormData({
      resourceName: "",
      selectedCase: null,
      contactId: "",
      contactName: "",
      currency: "INR",
      dueDate: "",
      particulars: [{ id: 1, description: "", amount: "" }],
      selectedExpenses: [],
      totalAmount: 0,
      invoiceNumber: "",
    });
    setCaseExpenses([]);
    setShowCaseDropdown(false);
    setCaseSearchTerm("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const renderContactSelect = () => {
    const selectedClient = clients.find((c) => c.id === formData.contactId);
    const displayName = selectedClient
      ? (selectedClient.salutation ? `${selectedClient.salutation} ${selectedClient.name}` : selectedClient.name)
      : "Select Contact";

    return (
      <div className="relative" ref={contactDropdownRef}>
        <button
          type="button"
          onClick={() => setShowContactDropdown(!showContactDropdown)}
          className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/40 transition-colors text-left flex items-center justify-between"
        >
          <span className={`truncate ${!selectedClient ? "text-slate-400" : "text-slate-900"}`}>{displayName}</span>
          <ChevronDown className={`ml-2 flex-shrink-0 transition-transform ${showContactDropdown ? "rotate-180" : ""}`} size={20} />
        </button>

        {showContactDropdown && (
          <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden flex flex-col">
            {clients.length === 0 ? (
              <div className="px-4 py-3 flex items-start gap-2">
                <Info size={15} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                <p className="text-yellow-600 text-xs leading-snug">
                  No client added. Please add a client from the{" "}
                  <button
                    type="button"
                    onClick={() => { setShowContactDropdown(false); onClose(); navigate("/portal/MyClients"); }}
                    className="font-semibold underline hover:text-yellow-700 transition-colors"
                  >
                    My Clients
                  </button>{" "}
                  section.
                </p>
              </div>
            ) : (
              <div className="overflow-y-auto max-h-52">
                {clients.map((client) => (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => { handleContactSelect(client.id); setShowContactDropdown(false); }}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0"
                  >
                    <div className="text-slate-800 text-sm">
                      {client.salutation ? `${client.salutation} ${client.name}` : client.name}
                      {client.company ? ` - ${client.company}` : ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-16 overflow-y-auto">
      <div className="invoice-modal bg-white rounded-xl border border-slate-200 shadow-2xl max-w-5xl w-full my-6">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-xl font-semibold text-slate-900">
            {isEditMode ? "Edit Invoice" : isSchedule ? "Schedule Invoice" : "Raise Invoice"}
          </h3>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-700 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[calc(100vh-10rem)] overflow-y-auto">
          {/* Type of Resource Toggle and Resource Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-600 text-sm font-medium mb-1.5">
                Type of Resource
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleResourceTypeChange("none")}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    resourceType === "none"
                      ? "bg-yellow-500 text-gray-900 border border-yellow-500"
                      : "bg-white border border-slate-300 text-slate-500 hover:text-slate-800 hover:border-slate-400"
                  }`}
                >
                  None
                </button>
                <button
                  type="button"
                  onClick={() => handleResourceTypeChange("case")}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    resourceType === "case"
                      ? "bg-yellow-500 text-gray-900 border border-yellow-500"
                      : "bg-white border border-slate-300 text-slate-500 hover:text-slate-800 hover:border-slate-400"
                  }`}
                >
                  Case
                </button>
              </div>
            </div>

            {/* Resource Name */}
            <div>
              <label className="block text-slate-600 text-sm font-medium mb-1.5">
                Resource Name
              </label>
              {resourceType === "none" ? (
                <input
                  type="text"
                  value={formData.resourceName}
                  onChange={(e) =>
                    setFormData({ ...formData, resourceName: e.target.value })
                  }
                  placeholder="Enter Name"
                  className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/40 transition-colors"
                />
              ) : (
                <div className="relative" ref={caseDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowCaseDropdown(!showCaseDropdown)}
                    className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/40 transition-colors text-left flex items-center justify-between"
                  >
                    <span className="truncate">
                      {formData.selectedCase
                        ? `${formData.selectedCase.caseNo} - ${formData.selectedCase.petitionerName} VS ${formData.selectedCase.respondentName}`
                        : "Select option"}
                    </span>
                    <ChevronDown className={`ml-2 flex-shrink-0 transition-transform ${showCaseDropdown ? 'rotate-180' : ''}`} size={20} />
                  </button>

                  {showCaseDropdown && (
                    <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-hidden flex flex-col">
                      {/* Search Input */}
                      <div className="p-2 border-b border-slate-100">
                        <input
                          type="text"
                          value={caseSearchTerm}
                          onChange={(e) => setCaseSearchTerm(e.target.value)}
                          placeholder="Search cases..."
                          className="w-full px-3 py-2 bg-white border border-slate-300 rounded text-slate-900 text-sm focus:outline-none focus:border-yellow-400"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Options List */}
                      <div className="overflow-y-auto max-h-52">
                        {getFilteredCases().length === 0 ? (
                          <div className="px-4 py-3 text-slate-400 text-sm">No cases found</div>
                        ) : (
                          getFilteredCases().map((caseItem) => (
                            <button
                              key={caseItem.id}
                              type="button"
                              onClick={() => handleCaseSelect(caseItem)}
                              className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0"
                            >
                              <div className="text-slate-800 text-sm line-clamp-2">
                                {caseItem.caseNo} - {caseItem.petitionerName} VS {caseItem.respondentName}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Contact (only for Case type) */}
          {resourceType === "case" && (
            <div>
              <label className="block text-slate-600 text-sm font-medium mb-1.5">
                Contact <span className="text-red-500">*</span>
              </label>
              {renderContactSelect()}
            </div>
          )}

          {/* Contact, Currency, Due Date (for None type) */}
          {resourceType === "none" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-slate-600 text-sm font-medium mb-1.5">
                  Contact <span className="text-red-500">*</span>
                </label>
                {renderContactSelect()}
              </div>

              <div>
                <label className="block text-slate-600 text-sm font-medium mb-1.5">
                  Select Currency
                </label>
                <SelectMenu
                  value={formData.currency}
                  onChange={(e) =>
                    setFormData({ ...formData, currency: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/40 transition-colors"
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </SelectMenu>
              </div>

              <div>
                <label className="block text-slate-600 text-sm font-medium mb-1.5">
                  Due On Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) =>
                    setFormData({ ...formData, dueDate: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/40 transition-colors [color-scheme:light]"
                  required
                />
              </div>
            </div>
          )}

          {/* Currency and Due Date (for Case type) */}
          {resourceType === "case" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-600 text-sm font-medium mb-1.5">
                  Select Currency
                </label>
                <SelectMenu
                  value={formData.currency}
                  onChange={(e) =>
                    setFormData({ ...formData, currency: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/40 transition-colors"
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </SelectMenu>
              </div>

              <div>
                <label className="block text-slate-600 text-sm font-medium mb-1.5">
                  Due On Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) =>
                    setFormData({ ...formData, dueDate: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/40 transition-colors [color-scheme:light]"
                  required
                />
              </div>
            </div>
          )}

          {/* Resource-Wise Particulars Section (for Case type) */}
          {resourceType === "case" && formData.selectedCase && (
            <div className="mt-6">
              <h4 className="text-center text-gray-400 text-sm mb-3">Resource-Wise Particulars</h4>

              {/* Case Name Banner */}
              <div
                className="bg-blue-500 text-white p-4 rounded-lg mb-4 flex items-center justify-between cursor-pointer hover:bg-blue-600 transition-colors"
                onClick={() => setShowResourceParticulars(!showResourceParticulars)}
              >
                <span className="font-medium">
                  {formData.selectedCase.caseNo} | {formData.selectedCase.petitionerName} VS {formData.selectedCase.respondentName}
                </span>
                {showResourceParticulars ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>

              {/* Case Expenses List */}
              {showResourceParticulars && caseExpenses.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                  <h5 className="text-slate-600 text-sm font-medium mb-3">Select Expenses</h5>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {caseExpenses.map((expense) => (
                      <label
                        key={expense.id}
                        className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <input
                            type="checkbox"
                            checked={formData.selectedExpenses.includes(expense.id)}
                            onChange={() => handleExpenseToggle(expense.id)}
                            className="w-4 h-4 accent-yellow-500 rounded"
                          />
                          <div className="flex-1">
                            <div className="text-slate-800 text-sm">{expense.type}</div>
                            {expense.description && (
                              <div className="text-slate-500 text-xs">{expense.description}</div>
                            )}
                            <div className="text-slate-400 text-xs">{expense.date}</div>
                          </div>
                        </div>
                        <div className="text-slate-800 font-medium">
                          {formData.currency === "INR" ? "₹" : formData.currency === "USD" ? "$" : "€"}
                          {parseFloat(expense.amount).toFixed(2)}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual Particulars for Case */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-slate-600 text-sm font-medium">
                    Particulars (in {formData.currency})
                  </label>
                  <button
                    type="button"
                    onClick={addParticular}
                    className="flex items-center gap-1 px-3 py-1 text-sm text-yellow-600 hover:text-yellow-700 font-medium transition-colors"
                  >
                    <Plus size={16} />
                    Add Particulars
                  </button>
                </div>

                <div className="space-y-3">
                  {formData.particulars.map((particular) => (
                    <div key={particular.id} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <input
                          type="text"
                          value={particular.description}
                          onChange={(e) =>
                            updateParticular(particular.id, "description", e.target.value)
                          }
                          placeholder="Enter particulars"
                          className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/40 transition-colors"
                        />
                      </div>

                      <div className="flex gap-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={particular.amount}
                          onChange={(e) =>
                            updateParticular(particular.id, "amount", e.target.value)
                          }
                          placeholder="Enter Amount"
                          className="flex-1 px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/40 transition-colors"
                        />
                        {formData.particulars.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeParticular(particular.id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            <Trash2 size={20} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Particulars Section - Only show when resourceType is "none" */}
          {resourceType === "none" && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-slate-600 text-sm font-medium">
                  Particulars (in {formData.currency})
                </label>
                <button
                  type="button"
                  onClick={addParticular}
                  className="flex items-center gap-1 px-3 py-1 text-sm text-yellow-600 hover:text-yellow-700 font-medium transition-colors"
                >
                  <Plus size={16} />
                  Add Particulars
                </button>
              </div>

              <div className="space-y-3">
                {formData.particulars.map((particular) => (
                  <div key={particular.id} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <input
                        type="text"
                        value={particular.description}
                        onChange={(e) =>
                          updateParticular(particular.id, "description", e.target.value)
                        }
                        placeholder="Enter particulars"
                        className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/40 transition-colors"
                        required
                      />
                    </div>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={particular.amount}
                        onChange={(e) =>
                          updateParticular(particular.id, "amount", e.target.value)
                        }
                        placeholder="Enter Amount"
                        className="flex-1 px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/40 transition-colors"
                        required
                      />
                      {formData.particulars.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeParticular(particular.id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                          <Trash2 size={20} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Total Amount */}
          <div className="flex justify-end pt-4 border-t border-slate-200">
            <div className="text-right">
              <span className="text-slate-500 text-sm">
                {resourceType === "case" ? "Resource Total: " : "Total Amount: "}
              </span>
              <span className="text-xl font-bold text-slate-900">
                {formData.currency === "INR" ? "₹" : formData.currency === "USD" ? "$" : "€"}{" "}
                {formData.totalAmount.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="px-5 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 font-medium text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 rounded-lg font-semibold text-sm transition-colors"
            >
              {isEditMode ? "Update Invoice" : isSchedule ? "Schedule" : "Raise"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RaiseInvoiceForm;
